<script setup lang="ts">
import { useEmitter } from "@/composition/useEmitter";
import { useAppStore } from "@/store/app";
import { GlobalSettingsEvents } from "@/util/constants";
import {
  defineAsyncMicroApp,
  unmountApp,
} from "@gohighlevel/ghl-module-federation-plugin";
import { UISpin } from "@gohighlevel/ghl-ui";
import {
  computed,
  markRaw,
  onBeforeUnmount,
  onMounted,
  ref,
  type Component,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

const QR_REMOTE = "qrCodeBuilderApp";
// Mirror of the QR remote's EXT_ROUTE_TO_BRAND_BOARDS event.
const QR_ROUTE_TO_BRAND_BOARDS = "ext-route-to-brand-boards";
const HOST_USER = Object.freeze({});

const props = defineProps<{ url: string }>();
const store = useAppStore();
const emitter = useEmitter();
const route = useRoute();
const { t, locale } = useI18n();

const remoteComponent = ref<Component | null>(null);
const isLoading = ref(true);
const loadFailed = ref(false);
let disposed = false;

const widget = computed(() => {
  if (store.formId) return { templateType: "FORM", id: store.formId };
  if (store.surveyId) return { templateType: "SURVEY", id: store.surveyId };
  return { templateType: "QUIZ", id: store.quizId };
});

// The remote editor reads these Vuex-shaped fields after its own lazy load.
const hostStore = {
  state: {
    locations: {
      get locations() {
        return [{ id: store.locationId, companyId: store.company?.id }];
      },
    },
  },
  getters: {
    get "company/get"() {
      return {
        id: store.company?.id,
        name: store.company?.name,
        logo_url: store.company?.logoURL,
        domain: store.company?.domain,
      };
    },
  },
};

const remoteProps = computed(() => ({
  emitter,
  locale: locale.value,
  show: true,
  surface: "panel",
  templateData: {
    url: props.url,
    id: widget.value.id,
    name: store.builder?.name ?? "",
  },
  locationId: store.locationId,
  templateTypes: [widget.value.templateType],
  templateType: widget.value.templateType,
  product: "forms",
  store: hostStore,
  user: HOST_USER,
}));

// The builder iframe has no clipboard permission; the QR remote asks the
// host to copy, and spm-ts copies at top level via the copyText bridge.
const copyViaHost = (link: string) => store.copyToClipBoard(link);
emitter.on("copy-to-clipboard", copyViaHost);

// The QR color picker's "Manage" asks for Brand Boards, which spm-ts owns;
// relay it the same way the builder's own color picker opens it.
const openBrandBoardsViaHost = () =>
  store.appHandshake?.then((parent) =>
    parent.emit(GlobalSettingsEvents.OPEN_GLOBAL_SETTINGS)
  );
emitter.on(QR_ROUTE_TO_BRAND_BOARDS, openBrandBoardsViaHost);

onMounted(async () => {
  // The editor resolves its tenant from the host route after the handoff.
  if (route) route.params.location_id = store.locationId;
  try {
    const [panel] = await defineAsyncMicroApp(QR_REMOTE, ["qr-integration"]);
    if (!panel) throw new Error("qr-integration export missing");
    if (!disposed) remoteComponent.value = markRaw(panel as Component);
  } catch (error) {
    console.error("Failed to load QR integration panel", error);
    if (!disposed) loadFailed.value = true;
  } finally {
    if (!disposed) isLoading.value = false;
  }
});

onBeforeUnmount(() => {
  disposed = true;
  emitter.off("copy-to-clipboard", copyViaHost);
  emitter.off(QR_ROUTE_TO_BRAND_BOARDS, openBrandBoardsViaHost);
  unmountApp(QR_REMOTE);
});
</script>

<template>
  <div class="qr-inline">
    <div
      v-if="isLoading"
      class="qr-inline__loading"
      data-testid="share-qr-loading"
      role="status"
    >
      <UISpin size="small" />
      <span>{{ t("integrate.share.qrLoading") }}</span>
    </div>
    <p v-else-if="loadFailed" data-testid="share-qr-error" role="alert">
      {{ t("integrate.share.qrLoadError") }}
    </p>
    <component
      :is="remoteComponent"
      v-else-if="remoteComponent"
      v-bind="remoteProps"
    />
  </div>
</template>

<style scoped>
.qr-inline {
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  container-type: inline-size;
}
.qr-inline :deep(.form-qr-footer) {
  flex-wrap: nowrap !important;
  gap: 8px !important;
}
.qr-inline :deep(#form-qr-download),
.qr-inline :deep(#form-qr-edit-qr),
.qr-inline :deep(#form-qr-copy-link) {
  --n-height: 36px !important;
  height: 36px !important;
  padding: 8px 14px !important;
  border-radius: 8px !important;
}
.qr-inline :deep(#form-qr-copy-link) {
  width: 36px;
  padding: 0 !important;
}
@container (max-width: 308px) {
  .qr-inline :deep(#form-qr-edit-qr .ml-2) {
    display: none;
  }
  .qr-inline :deep(#form-qr-edit-qr) {
    width: 36px;
    padding: 0 !important;
  }
}
.qr-inline__loading {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
